/**
 * FinancialLiteracy — gated, standalone Learn → Test → Certify experience.
 *
 * A NEW non-ESSLCE vertical riding the existing micro-learning rails. Lives on
 * its own unlinked route (/finance) so regular students never see it; access is
 * further gated by a shared key so it's "only certain people". Reuses the
 * MicroLearningModule player verbatim and adds a completion Certificate.
 *
 * Removal = delete this page + Certificate folder + the /finance route, and run
 * the seed script's DELETE block. Nothing in the shared ESSLCE flow is touched.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/authFetch';
import MicroLearningModule from '../components/MicroLearning/MicroLearningModule';
import Certificate from '../components/finance/Certificate';
import Logo from '../components/Logo';
import './FinancialLiteracy.css';

const SUBJECT = 'Financial Literacy';
const MODULE_ID = 'module_finlit_mpesa_basics';
// Shared access key — give the link ?k=<key> to specific people. Overridable
// via REACT_APP_FINLIT_KEY at build time. This + the unlinked route is the gate.
const ACCESS_KEY = process.env.REACT_APP_FINLIT_KEY || 'lomi-money-2026';
const ACCESS_STORE = 'finlit_access_ok';

interface Summary {
  module?: { title?: string };
  performance?: {
    status?: string;
    accuracy?: number;
    correct_answers?: number;
    total_questions?: number;
  };
  timestamps?: { completed_at?: string };
}

function shortVerifyId(studentId: string): string {
  // Deterministic short code from the student id (stable per person).
  let h = 0;
  const s = `${studentId}:${MODULE_ID}`;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return `LOMI-FL-${h.toString(36).toUpperCase().padStart(6, '0').slice(0, 6)}`;
}

const FinancialLiteracy: React.FC = () => {
  const { student } = useAuth();
  const location = useLocation();

  const [accessOk, setAccessOk] = useState<boolean>(false);
  const [accessChecked, setAccessChecked] = useState(false);

  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [showCert, setShowCert] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- Access gate ---------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get('k');
    if (key && key === ACCESS_KEY) {
      try { localStorage.setItem(ACCESS_STORE, '1'); } catch {}
      setAccessOk(true);
    } else if (localStorage.getItem(ACCESS_STORE) === '1') {
      setAccessOk(true);
    }
    setAccessChecked(true);
  }, [location.search]);

  // --- Load module progress (offer "view certificate" if already done) -----
  useEffect(() => {
    if (!accessOk || !student?.student_id) return;
    (async () => {
      try {
        setLoading(true);
        const res = await authFetch(
          `/api/micro-learning/modules?subject=${encodeURIComponent(SUBJECT)}&status=published&student_id=${student.student_id}`
        );
        const data = await res.json();
        const mod = (data.modules || []).find((m: any) => m.module_id === MODULE_ID);
        if (mod?.student_progress?.status === 'completed') {
          setCompleted(true);
        }
      } catch {
        /* non-critical */
      } finally {
        setLoading(false);
      }
    })();
  }, [accessOk, student?.student_id]);

  const studentName =
    student?.display_name ||
    student?.profile_name ||
    [student?.first_name, student?.last_name].filter(Boolean).join(' ') ||
    'Lomi Learner';

  const fetchSummaryAndShowCert = async () => {
    if (!student?.student_id) return;
    try {
      const res = await authFetch(
        `/api/micro-learning/modules/${MODULE_ID}/summary?student_id=${student.student_id}`
      );
      const data = await res.json();
      setSummary(data);
    } catch {
      /* fall back to zeros */
    }
    setShowCert(true);
  };

  const handleModuleComplete = (s: Summary) => {
    setSummary(s);
    setStarted(false);
    setCompleted(true);
    setShowCert(true);
  };

  // --- Gate screens --------------------------------------------------------
  if (!accessChecked) return null;

  if (!accessOk) {
    return (
      <div className="finlit-gate">
        <div className="finlit-gate-card">
          <div className="finlit-lock">🔒</div>
          <h2>Private preview</h2>
          <p>This Lomi course is invite-only. Open the link you were given, including its access key.</p>
        </div>
      </div>
    );
  }

  // --- Active module player ------------------------------------------------
  if (started && student?.student_id) {
    return (
      <div className="finlit-player">
        <MicroLearningModule
          studentId={student.student_id}
          subject={SUBJECT}
          moduleId={MODULE_ID}
          onModuleComplete={handleModuleComplete}
          onExit={() => setStarted(false)}
        />
      </div>
    );
  }

  const perf = summary?.performance;
  const verifyId = shortVerifyId(student?.student_id || 'anon');

  // --- Landing -------------------------------------------------------------
  return (
    <div className="finlit-page">
      <header className="finlit-hero">
        <div className="finlit-logo"><Logo height={34} withAmharic /></div>
        <div className="finlit-badge">FINANCIAL LITERACY · PILOT</div>
        <h1>Money & Mobile Confidence</h1>
        <p className="finlit-tagline">
          Learn to use mobile money safely, avoid scams, understand fees, and build the
          record that opens the door to credit — then earn your certificate.
        </p>
      </header>

      <div className="finlit-course-card">
        <div className="finlit-course-top">
          <span className="finlit-course-emoji">💰</span>
          <div>
            <h2>M-PESA &amp; Money Basics</h2>
            <div className="finlit-course-meta">
              ~12 min · Lessons · Practice · Final test · Certificate
            </div>
          </div>
        </div>

        <ul className="finlit-objectives">
          <li>Check your balance and send money safely on <code>*733#</code></li>
          <li>Spot and stop common M-PESA scams</li>
          <li>Explain fees to a customer with confidence</li>
          <li>Build a digital footprint that qualifies you for credit</li>
        </ul>

        <div className="finlit-steps">
          <span className="finlit-step">📖 Learn</span>
          <span className="finlit-arrow">→</span>
          <span className="finlit-step">📝 Test</span>
          <span className="finlit-arrow">→</span>
          <span className="finlit-step">🏆 Certify</span>
        </div>

        {loading ? (
          <button className="finlit-cta" disabled>Loading…</button>
        ) : completed ? (
          <div className="finlit-cta-row">
            <button className="finlit-cta finlit-cta-cert" onClick={fetchSummaryAndShowCert}>
              🏆 View my certificate
            </button>
            <button className="finlit-cta finlit-cta-ghost" onClick={() => setStarted(true)}>
              Review course
            </button>
          </div>
        ) : (
          <button className="finlit-cta" onClick={() => setStarted(true)}>
            Start course →
          </button>
        )}

        <div className="finlit-amharic-note">🇪🇹 Tap “በአማርኛ” inside any lesson to read it in Amharic.</div>
      </div>

      <footer className="finlit-foot">
        Lomi pilot · Financial literacy training, testing &amp; certification
      </footer>

      {showCert && (
        <Certificate
          studentName={studentName}
          courseTitle="M-PESA & Money Basics"
          accuracy={perf?.accuracy ?? 0}
          correctAnswers={perf?.correct_answers ?? 0}
          totalQuestions={perf?.total_questions ?? 0}
          completedAt={summary?.timestamps?.completed_at}
          verifyId={verifyId}
          onClose={() => setShowCert(false)}
        />
      )}
    </div>
  );
};

export default FinancialLiteracy;
